import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { ReactComponent as ArrowLeftIcon } from '../../../assets/icons/arrow-left.svg';
import { ReactComponent as CreditCardIcon } from '../../../assets/icons/credit-card.svg';
import DefaultApiLimit from '../../../config/defaultLimit';
import {
  PaymentTransactionRecord,
  RequestPaymentTransactionsInterface,
} from '../../../interface/payment-transaction-interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { setPageTitle } from '../../../redux/slices/PageTitleSlice';
import {
  disablePaymentTransactionFilterApplied,
  fetchPaymentTransactions,
  clearPaymentTransactionData,
  updatePaymentTransactionFilters,
  fetchPaymentTransactionFilters,
  setSortData,
  setGatewayFilterOptions,
  refreshTranstion,
} from '../../../redux/slices/PaymentTransactionSlice';
import CustomTable from '../../../shared-components/custom-table/CustomTable';
import { GetParametersFromCommonFilters } from '../../../shared-functions/CommonFilters';
import ToFixedValue from '../../../shared-functions/ToFixedValue';
import paymentGatewayApi from '../../../utils/api/v2/payment-gateway';

/**
 * Get gateway name helper
 * @param {string} key - gateway key name
 * @returns {string} display name
 */
const getGatewayName = (key: string) => {
  const names: Record<string, string> = {
    stripe: 'Stripe',
    hyperpay: 'HyperPay',
    checkoutcom: 'Checkout.com',
    urway: 'Urway',
  };
  return names[key.toLowerCase()] || key;
};

/**
 * Map payment method string to valid transaction type
 * @param {string} method - payment method string
 * @returns {string | null} mapped transaction type or null
 */
const mapPaymentMethodToTransactionType = (method: string): string | null => {
  const lower = method.toLowerCase();
  if (lower === 'preauth') return 'preAuth';
  if (lower === 'debit') return 'debit';
  if (lower === 'credit') return 'credit';
  if (lower === 'refund') return 'refund';
  if (lower === 'payout') return 'payout';

  if (lower.includes('credit')) return 'credit';
  if (lower.includes('debit')) return 'debit';
  if (lower.includes('mada')) return 'debit';
  if (lower.includes('apple')) return 'debit';
  if (lower.includes('paypal')) return 'debit';
  if (lower.includes('preauth')) return 'preAuth';
  if (lower.includes('payout')) return 'payout';
  return null;
};

/**
 * Payment Transactions list component
 * @returns The rendered component
 */
function PaymentTransactions() {
  const navigate = useNavigate();
  const [searchParameters] = useSearchParams();
  const gatewayParameter = searchParameters.get('gateway');
  const typeParameter = searchParameters.get('type');
  const dispatch = useAppDispatch();

  const {
    records,
    limit,
    page,
    loading,
    totalPages,
    totalResults,
    filters,
    filterListLoading,
    isFilterUpdated,
    sortBy,
    sortField,
  } = useAppSelector((state) => state.paymentTransactions);

  /**
   * Handle the fetch data API request
   * @param {RequestPaymentTransactionsInterface} queryData - query filters and pagination params
   */
  const fetchData = (queryData: RequestPaymentTransactionsInterface) => {
    if (!loading) {
      dispatch(fetchPaymentTransactions(queryData));
    }
  };

  /**
   * Handle manual refresh
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      page,
      limit,
      ...queryParameters,
    });
  };

  /**
   * Handle pagination limit/page change
   * @param {RequestPaymentTransactionsInterface} value - pagination values
   */
  const pageNateLimitChange = (value: RequestPaymentTransactionsInterface) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({ ...queryParameters, ...value });
  };

  /**
   * Handle sort change
   * sort field and direction
   */
  const handleSortFunction = (sortData: { sortField: string; sortBy: 'asc' | 'desc' }) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  /**
   * Function for handling auto refresh
   * Each 10 sec triggers auto refresh from header component
   */
  const handleAutoRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);

    dispatch(
      refreshTranstion({
        ...queryParameters,
        page,
        limit,
        sortBy,
        sortField,
      }),
    );
  };

  useEffect(() => {
    /**
     * Load configured gateways dynamically to populate dropdown filters
     */
    const loadConfiguredGateways = async () => {
      try {
        const response = await paymentGatewayApi.fetchPaymentGatewayConfigList();
        const configList = response.data || [];
        const options = configList.map((g: any) => ({
          label: getGatewayName(g.gatewayKey),
          value: g.gatewayKey,
        }));
        dispatch(setGatewayFilterOptions(options));
      } catch (error) {
        console.error('Failed to load configured gateways', error);
      }
    };

    loadConfiguredGateways();
  }, [dispatch]);

  useEffect(() => {
    if (gatewayParameter) {
      dispatch(
        updatePaymentTransactionFilters({
          type: 'add',
          key: 'gateway',
          value: [gatewayParameter],
        }),
      );
    }
  }, [gatewayParameter, dispatch]);

  useEffect(() => {
    if (typeParameter) {
      const mappedTypes = typeParameter
        .split(',')
        .map((method) => mapPaymentMethodToTransactionType(method.trim()))
        .filter((type): type is string => type !== null);

      const uniqueTypes = Array.from(new Set(mappedTypes));

      if (uniqueTypes.length > 0) {
        dispatch(
          updatePaymentTransactionFilters({
            type: 'add',
            key: 'type',
            value: uniqueTypes,
          }),
        );
      }
    }
  }, [typeParameter, dispatch]);

  useEffect(() => {
    const hasUrlParameters = gatewayParameter || typeParameter;
    const isReady = hasUrlParameters ? isFilterUpdated : true;

    if (isReady && (isFilterUpdated || records.length === 0)) {
      const queryParameters = GetParametersFromCommonFilters(filters);
      fetchData({
        page: DefaultApiLimit.page,
        limit,
        ...queryParameters,
      });
      dispatch(disablePaymentTransactionFilterApplied());
    }
  }, [filters, gatewayParameter, typeParameter, isFilterUpdated]);

  useEffect(() => {
    dispatch(
      setPageTitle({
        title: 'Payment Transactions',
        icon: <CreditCardIcon />,
        count: totalResults,
      }),
    );
  }, [totalResults, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(clearPaymentTransactionData());
    };
  }, [dispatch]);

  const columns = [
    {
      label: 'Transaction ID',
      accessorKey: 'transactionId' as keyof PaymentTransactionRecord,
      sortAllow: false,
    },
    {
      label: 'Driver ID',
      accessorKey: 'userCustomId' as keyof PaymentTransactionRecord,
      sortAllow: false,
      /**
       * Render custom user ID
       * @param {any} row - row data
       * @returns  The rendered node
       */
      customData: (row: any) => <span>{row.userCustomId || 'N/A'}</span>,
    },
    {
      label: 'Type',
      accessorKey: 'type' as keyof PaymentTransactionRecord,
      sortAllow: false,
      /**
       * Render type badge
       * @param {any} row - row data
       * @returns  The rendered node
       */
      customData: (row: any) => (
        <span className={`transaction-type-badge ${row.type?.toLowerCase() || ''}`}>
          {row.type}
        </span>
      ),
    },
    {
      label: 'Gateway',
      accessorKey: 'gateway' as keyof PaymentTransactionRecord,
      sortAllow: false,
      /**
       * Render gateway badge
       * @param {any} row - row data
       * @returns The rendered node
       */
      customData: (row: any) => <span className="gateway-text-badge">{row.gateway}</span>,
    },
    {
      label: 'Amount',
      accessorKey: 'amount' as keyof PaymentTransactionRecord,
      sortAllow: false,
      /**
       * Render amount and currency
       * @param {any} row - row data
       * @returns The rendered node
       */
      customData: (row: any) => (
        <span className="amount-text">
          {ToFixedValue(row.amount)} {row.currency}
        </span>
      ),
    },
    {
      label: 'Status',
      accessorKey: 'status' as keyof PaymentTransactionRecord,
      sortAllow: false,
      /**
       * Render status badge
       * @param {any} row - row data
       * @returns The rendered node
       */
      customData: (row: any) => (
        <span className={`status-badge ${row.status?.toLowerCase() || ''}`}>{row.status}</span>
      ),
    },
    {
      label: 'Gateway Tx ID',
      accessorKey: 'gatewayTransactionId' as keyof PaymentTransactionRecord,
      sortAllow: false,
      /**
       * Render gateway transaction ID
       * @param {any} row - row data
       * @returns The rendered node
       */
      customData: (row: any) => <span>{row.gatewayTransactionId || 'N/A'}</span>,
    },
  ];

  return (
    <div className="payment-transactions-container">
      <div className="header-section">
        <div className="header-content">
          <ArrowLeftIcon
            className="back-arrow"
            onClick={() => navigate('/ev-admin/configuration/PaymentGateway')}
          />
          <div className="header-title-wrap">
            <h2>Payment Transactions</h2>
            <div className="divider" />
            <p>View and search payment transactions across tenant operators</p>
          </div>
        </div>
      </div>

      <div className="table-wrapper">
        <CustomTable
          header={columns}
          data={records}
          version="v2"
          serialNo
          limit={limit}
          page={page}
          loading={loading}
          totalPages={totalPages}
          totalResults={totalResults}
          sortData={{ sortBy, sortField }}
          handleSortChange={handleSortFunction}
          pageLimitChange={pageNateLimitChange}
          sizeChangeButtonRequired
          filters={filters}
          filtersToDispatch={updatePaymentTransactionFilters}
          filterListLoading={filterListLoading}
          filterListToDispatch={fetchPaymentTransactionFilters}
          requiredRefreshToggle
          refreshFunction={handleAutoRefresh}
          refreshRequired={false}
          height={350}
          handleRefresh={handleRefresh}
          created
          lastUpdated
        />
      </div>
    </div>
  );
}

export default PaymentTransactions;
